import { useNavigate } from '@tanstack/react-router'
import { Calendar, MapPin } from 'lucide-react'
import type { Event } from '@/lib/types'
import { Card, Stack, spacing } from '@/components/ui'
import EventBanner from '@/components/EventBanner'
import DateWarning from '@/components/DateWarning'
import {
  isLikelyInvalidDate,
  shortDate,
  shortTime,
  splitAddress,
} from '@/lib/helpers'

export default function EventCard({
  event,
  compact = false,
  active = false,
  onHover,
  preview = false,
}: {
  event: Event
  compact?: boolean
  active?: boolean
  onHover?: (id: number | null) => void
  /** Render as a non-clickable live preview (used in the propose form). */
  preview?: boolean
}) {
  const navigate = useNavigate()
  const { venue, city } = splitAddress(event.location.address)
  const badDate = isLikelyInvalidDate(event.start_date)
  const open = () => {
    if (!preview)
      navigate({ to: '/events/$id', params: { id: String(event.id) } })
  }

  return (
    <Card
      padding={0}
      hoverable={!preview}
      onClick={open}
      onMouseEnter={() => onHover?.(event.id)}
      onMouseLeave={() => onHover?.(null)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        cursor: preview ? 'default' : 'pointer',
        outline: active ? '2px solid var(--emerald-600)' : 'none',
        outlineOffset: 2,
        overflow: 'hidden',
      }}
    >
      <EventBanner event={event} aspectRatio="4 / 3" />
      <Stack
        gap="sm"
        style={{ padding: compact ? spacing.lg : spacing.xl, flex: 1 }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: compact ? 18 : 21,
            lineHeight: 1.2,
            color: 'var(--ink-0)',
          }}
        >
          {event.title}
        </h3>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', fontWeight: 500 }}>
          <Calendar size={15} />{' '}
          {badDate ? (
            <DateWarning compact />
          ) : (
            <>
              {shortDate(event.start_date)} · {shortTime(event.start_date)}
            </>
          )}
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)' }}>
          <MapPin size={15} /> {venue}
          {city && ` · ${city}`}
        </div>
      </Stack>
    </Card>
  )
}
